/* Native Shopify data and links only; no mock cart, prices or checkout. */
(() => {
  'use strict';
  const instances = new Map();
  function initialize(root) {
    if (instances.has(root)) return;
    const abort = new AbortController();
    const disposers = [];
    const on = (node, event, fn, options = {}) => node?.addEventListener(event, fn, {...options, signal: abort.signal});
    const footerGroups = [...root.querySelectorAll('[data-hd-footer-group]')];
    if (footerGroups.length) {
      const phone = matchMedia('(max-width:700px)');
      const setGroups = () => footerGroups.forEach(group => { group.open = !phone.matches; });
      setGroups();
      on(phone, 'change', setGroups);
      footerGroups.forEach(group => on(group.querySelector('summary'), 'click', event => {
        if (!phone.matches) event.preventDefault();
      }));
    }
    root.querySelectorAll('[data-hd-crop]').forEach(box => {
      const image = box.querySelector('img');
      const crop = () => {
        if (!image?.naturalWidth) return;
        const [x,y,w,h] = box.dataset.hdCrop.split(',').map(Number);
        if (![x,y,w,h].every(Number.isFinite) || w <= 0 || h <= 0) return;
        const scale = Math.max(box.clientWidth/(image.naturalWidth*w), box.clientHeight/(image.naturalHeight*h));
        Object.assign(image.style, {width:`${image.naturalWidth*scale}px`,height:`${image.naturalHeight*scale}px`,left:`${(box.clientWidth-image.naturalWidth*w*scale)/2-x*image.naturalWidth*scale}px`,top:`${(box.clientHeight-image.naturalHeight*h*scale)/2-y*image.naturalHeight*scale}px`});
      };
      on(image,'load',crop); const observer = new ResizeObserver(crop); observer.observe(box); crop();
      disposers.push(() => observer.disconnect());
    });
    // The opening crop is ready now. Do not wait for unrelated theme modules
    // to reveal this server-rendered LCP content.
    root.classList.add('hd-ready');
    on(root,'click',event => {
      const flip = event.target.closest('[data-hd-flip-toggle]');
      if (flip) {
        const stage = flip.closest('[data-hd-flip]');
        const reversed = stage.classList.toggle('is-reversed');
        flip.setAttribute('aria-pressed',String(reversed));
        stage.querySelector('.hd-flip-front')?.setAttribute('aria-hidden',String(reversed));
        stage.querySelector('.hd-flip-reverse')?.setAttribute('aria-hidden',String(!reversed));
      }
      const alternate = event.target.closest('[data-hd-image-toggle]');
      if (alternate) toggleCard(alternate.closest('[data-hd-alternate]'));
      // Product links always navigate on the first tap. Alternate photographs
      // remain available through the separate button and desktop hover.
    });
    function toggleCard(card) {
      card.dataset.hdImageChosen = 'true';
      const active = card.classList.toggle('is-alternate');
      card.querySelector('[data-hd-image-toggle]').setAttribute('aria-pressed',String(active));
    }
    root.querySelectorAll('.hd-flip-reverse').forEach(img => img.setAttribute('aria-hidden','true'));
    const arrivals = root.matches('[data-hd-arrivals]') ? root : root.querySelector('[data-hd-arrivals]');
    if (arrivals) {
      const track = arrivals.querySelector('[data-hd-track]');
      const motion = arrivals.querySelector('[data-hd-motion]');
      if (track && motion) {
        let timer, running = false, inView = false;
        const reduced = matchMedia('(prefers-reduced-motion: reduce)');
        const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
        const move = (step, automatic = false) => {
          if (automatic && (!inView || document.hidden || arrivals.contains(document.activeElement) || arrivals.matches(':hover'))) return;
          const distance = (track.firstElementChild?.getBoundingClientRect().width || track.clientWidth) + parseFloat(getComputedStyle(track).gap || 0);
          const end = Math.abs(track.scrollLeft) >= track.scrollWidth-track.clientWidth-4;
          if (step > 0 && end) track.scrollTo({left:0,behavior:reduced.matches?'auto':'smooth'});
          else track.scrollBy({left:step*direction*distance,behavior:reduced.matches?'auto':'smooth'});
        };
        const stop = () => { running=false; clearInterval(timer); motion.setAttribute('aria-pressed','false'); motion.textContent=motion.dataset.play; };
        on(motion,'click',() => {
          if (running) return stop();
          running=true; motion.setAttribute('aria-pressed','true'); motion.textContent=motion.dataset.pause;
          timer=setInterval(() => move(1,true),5500);
        });
        on(arrivals.querySelector('[data-hd-prev]'),'click',() => { stop(); move(-1); });
        on(arrivals.querySelector('[data-hd-next]'),'click',() => { stop(); move(1); });
        on(track,'pointerdown',stop); on(track,'keydown',stop); on(reduced,'change',stop);
        const observer = new IntersectionObserver(entries => { inView=entries[0].isIntersecting; }); observer.observe(arrivals);
        disposers.push(() => { clearInterval(timer); observer.disconnect(); });
        // Fisher–Yates: each collection product participates; no fixed curated subset.
        const shuffle = () => {
          if (arrivals.contains(document.activeElement)) return;
          const cards = [...track.children];
          for (let i=cards.length-1;i>0;i--) { const j=Math.floor(Math.random()*(i+1)); [cards[i],cards[j]]=[cards[j],cards[i]]; }
          track.append(...cards);
        };
        shuffle();
        // Reordering snap targets can retain the formerly first card's new
        // offset. Start the randomized collection at its actual first card,
        // without animating past products before the customer interacts.
        track.scrollTo({left:0,behavior:'instant'});
        // Shopify paginates Liquid collections at 250. Follow any further pages
        // with Section Rendering, preserving locale and the preview template.
        (async () => {
          let next=track.dataset.next; const visited=new Set();
          while (next && !abort.signal.aborted) {
            const url=new URL(next,location.href);
            if (url.origin!==location.origin || visited.has(url.href)) break;
            visited.add(url.href); url.searchParams.set('section_id',arrivals.dataset.sectionId);
            url.searchParams.delete('view');
            const response=await fetch(url,{signal:abort.signal}); if (!response.ok) break;
            const doc=new DOMParser().parseFromString(await response.text(),'text/html');
            const incoming=doc.querySelector('[data-hd-track]'); if (!incoming) break;
            const known=new Set([...track.children].map(card=>card.dataset.hdProduct));
            for (const card of incoming.children) if (!known.has(card.dataset.hdProduct)) track.append(card.cloneNode(true));
            next=incoming.dataset.next; shuffle();
          }
        })().catch(() => { /* Server-rendered cards and collection link remain usable. */ });
      }
    }
    const craft = root.matches('[data-hd-craft]') ? root : root.querySelector('[data-hd-craft]');
    if (craft) {
      const img=craft.querySelector('.hd-art img'), caption=craft.querySelector('[data-hd-shot-caption]');
      const shots=[...craft.querySelectorAll('[data-hd-shot]')], play=craft.querySelector('[data-hd-craft-play]');
      const reduced=matchMedia('(prefers-reduced-motion: reduce)');
      let paused=reduced.matches, visible=false, focused=false, timer=null;
      const show=shot => {
        if (!shot || !img) return;
        img.removeAttribute('srcset'); img.src=shot.dataset.hdShot; img.alt=shot.dataset.hdAlt;
        shots.forEach(button=>button.setAttribute('aria-pressed',String(button===shot)));
        if (caption) caption.textContent=shot.dataset.hdCaption || '';
      };
      const sync=() => {
        clearTimeout(timer); timer=null;
        if(play) { play.hidden=shots.length<2; play.textContent=paused ? play.dataset.play : play.dataset.pause; }
        const running=!paused && visible && !focused && !document.hidden && shots.length>1;
        caption?.setAttribute('aria-live',running?'off':'polite');
        if(running) timer=setTimeout(()=>{const index=shots.findIndex(shot=>shot.getAttribute('aria-pressed')==='true');show(shots[(index+1)%shots.length]);sync();},2000);
      };
      on(craft,'click',event => {
        const shot=event.target.closest('[data-hd-shot]');
        if (shot && img) { show(shot);sync(); }
      });
      on(play,'click',()=>{paused=!paused;sync();});
      // Mouse hover and touch selection must not silently stop the slideshow.
      // Pause temporarily for keyboard navigation; the explicit button controls lasting pauses.
      on(craft,'focusin',event=>{focused=event.target!==play && event.target.matches(':focus-visible');sync();});
      on(craft,'focusout',()=>{focused=false;sync();});
      on(document,'visibilitychange',sync);
      on(reduced,'change',()=>{if(reduced.matches)paused=true;sync();});
      const inView=new IntersectionObserver(entries=>{visible=entries[0].isIntersecting && entries[0].intersectionRatio>=0.1;sync();},{threshold:[0,0.1]});
      inView.observe(craft.querySelector('.hd-craft-screen'));sync();
      disposers.push(()=>{clearTimeout(timer);inView.disconnect();});
    }
    instances.set(root,() => { abort.abort(); disposers.forEach(dispose=>dispose()); });
  }
  function scan(scope=document) { scope.querySelectorAll('.hd[id^="Henia-"]').forEach(initialize); }
  scan();
  document.addEventListener('shopify:section:load',event=>scan(event.target));
  document.addEventListener('shopify:section:unload',event=>{
    for (const [root,dispose] of instances) if (event.target.contains(root)) { dispose(); instances.delete(root); }
  });
})();
